/**
 * BrainActivityFeed — "What Your Brain Did Today" timeline.
 * Fetches from /api/brain/activity-feed and /api/brain/stats.
 * Items are clickable and navigate to relevant pages.
 * Supports type + time range filtering.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Mail, Target, ListChecks, GitBranch,
  ChevronDown, ChevronUp, Zap, ExternalLink
} from 'lucide-react';
import api from '../../lib/api';

const ACTION_CONFIG = {
  decision: { icon: GitBranch, color: 'text-violet-400', bg: 'bg-violet-500/10', activeBg: 'bg-violet-500/20 ring-1 ring-violet-400/30', label: 'Decisions', route: '/pipeline-health' },
  email:    { icon: Mail,      color: 'text-cyan-400',   bg: 'bg-cyan-500/10',   activeBg: 'bg-cyan-500/20 ring-1 ring-cyan-400/30',   label: 'Emails',    route: '/ai-agents/cadence-analytics' },
  task:     { icon: ListChecks, color: 'text-amber-400',  bg: 'bg-amber-500/10',  activeBg: 'bg-amber-500/20 ring-1 ring-amber-400/30', label: 'Tasks',     route: '/schedule/tasks' },
  followup: { icon: Target,    color: 'text-emerald-400', bg: 'bg-emerald-500/10', activeBg: 'bg-emerald-500/20 ring-1 ring-emerald-400/30', label: 'Follow-ups', route: '/leads' },
};

const TIME_RANGES = [
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'This Week' },
  { key: 'all', label: 'All Time' },
];

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 172800) return 'yesterday';
  return `${Math.floor(diff / 86400)}d ago`;
}

function isWithinRange(dateStr, range) {
  if (range === 'all' || !dateStr) return true;
  const now = new Date();
  const d = new Date(dateStr);
  if (range === 'today') {
    return d.toDateString() === now.toDateString();
  }
  if (range === 'week') {
    const weekAgo = new Date(now);
    weekAgo.setDate(weekAgo.getDate() - 7);
    return d >= weekAgo;
  }
  return true;
}

function ActivityItem({ item, index, onClick }) {
  const config = ACTION_CONFIG[item.type] || ACTION_CONFIG.decision;
  const Icon = config.icon;
  const route = item.leadId ? `/leads/${item.leadId}` : config.route;

  return (
    <div
      onClick={() => onClick(route)}
      className="flex items-start gap-3 py-2.5 px-1 -mx-1 rounded-lg cursor-pointer group transition-all hover:bg-slate-50 dark:hover:bg-white/[0.03] animate-[activityIn_0.3s_ease-out_both]"
      style={{ animationDelay: `${index * 60}ms` }}
      data-testid={`activity-item-${index}`}
    >
      <div className={`w-7 h-7 rounded-lg ${config.bg} flex items-center justify-center shrink-0 mt-0.5 transition-transform group-hover:scale-110`}>
        <Icon className={`h-3.5 w-3.5 ${config.color}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-slate-700 dark:text-white/70 leading-relaxed group-hover:text-slate-900 dark:group-hover:text-white/90 transition-colors">{item.summary}</p>
        <div className="flex items-center gap-2 mt-0.5">
          <span className={`text-[11px] font-medium ${config.color}`}>{ACTION_CONFIG[item.type]?.label?.replace(/s$/, '')}</span>
          <span className="text-[11px] text-slate-400 dark:text-white/25">{timeAgo(item.created_at)}</span>
        </div>
      </div>
      <ExternalLink className="h-3 w-3 text-slate-300 dark:text-white/15 opacity-0 group-hover:opacity-100 transition-opacity shrink-0 mt-1" />
    </div>
  );
}

export default function BrainActivityFeed() {
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [stats, setStats] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [timeRange, setTimeRange] = useState('all');

  useEffect(() => {
    const fetchFeed = async () => {
      try {
        const [feedRes, statsRes] = await Promise.allSettled([
          api.get('/api/brain/activity-feed?limit=15'),
          api.get('/api/brain/stats'),
        ]);
        const feed = feedRes.status === 'fulfilled' ? feedRes.value.data : {};
        const st = statsRes.status === 'fulfilled' ? statsRes.value.data : {};
        setStats(st);

        const items = [];
        (feed.decisions || []).forEach(d => {
          // Skip noise like "Periodic pipeline analysis completed"
          const summary = d.output_summary || d.reasoning || d.action?.replace('brain:', '').replace(/_/g, ' ');
          if (summary && !summary.toLowerCase().includes('periodic') && !summary.toLowerCase().includes('no action needed')) {
            items.push({
              type: 'decision', summary, created_at: d.created_at, leadId: d.lead_id, leadName: d.lead_name,
            });
          }
        });
        (feed.ai_email_drafts || []).forEach(d => items.push({
          type: 'email',
          summary: `${d.status === 'sent' ? 'Sent' : 'Drafted'}: "${d.subject || 'Untitled'}"${d.lead_name ? ` to ${d.lead_name}` : ''}`,
          created_at: d.created_at, leadId: d.lead_id, leadName: d.lead_name,
        }));
        (feed.ai_tasks || []).forEach(d => items.push({
          type: 'task',
          summary: `${d.title}${d.lead_name ? ` — ${d.lead_name}` : ''}`,
          created_at: d.created_at, leadId: d.lead_id, leadName: d.lead_name,
        }));
        (feed.ai_followups || []).forEach(d => items.push({
          type: 'followup',
          summary: d.notes?.substring(0, 80) || `Follow-up scheduled${d.date ? ` for ${new Date(d.date).toLocaleDateString()}` : ''}`,
          created_at: d.created_at, leadId: d.lead_id, leadName: d.lead_name,
        }));

        items.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setActivities(items);
      } catch { /* silent */ }
      finally { setLoading(false); }
    };
    fetchFeed();
  }, []);

  // Compute counts per type for the current time range
  const typeCounts = useMemo(() => {
    const counts = { all: 0, decision: 0, email: 0, task: 0, followup: 0 };
    activities.forEach(a => {
      if (isWithinRange(a.created_at, timeRange)) {
        counts.all++;
        counts[a.type] = (counts[a.type] || 0) + 1;
      }
    });
    return counts;
  }, [activities, timeRange]);

  // Apply both filters
  const filtered = useMemo(() => {
    return activities.filter(a => {
      if (typeFilter !== 'all' && a.type !== typeFilter) return false;
      if (!isWithinRange(a.created_at, timeRange)) return false;
      return true;
    });
  }, [activities, typeFilter, timeRange]);

  const handleItemClick = (route) => navigate(route);

  if (loading || activities.length === 0) return null;

  const visible = expanded ? filtered : filtered.slice(0, 5);
  const hasMore = filtered.length > 5;

  return (
    <div className="mt-6 animate-[fadeUp_0.5s_ease-out]" data-testid="brain-activity-feed">
      <style>{`
        @keyframes activityIn { from { opacity: 0; transform: translateX(-6px); } to { opacity: 1; transform: translateX(0); } }
      `}</style>

      <div className="rounded-2xl border border-white/20 dark:border-white/[0.08] bg-white/70 dark:bg-white/[0.06] backdrop-blur-xl overflow-hidden shadow-sm">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100 dark:border-slate-700/40">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-sm">
              <Zap className="h-4 w-4 text-white" />
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-900 dark:text-white tracking-tight">What Your Brain Did</h3>
              {stats && (
                <p className="text-[11px] text-slate-400 dark:text-white/30">
                  {stats.total_decisions || 0} decisions · {stats.total_pending_actions || 0} pending actions
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1 text-[10px] text-emerald-500 font-medium">
            <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" /></span>
            Brain Active
          </div>
        </div>

        {/* Filter bar */}
        <div className="px-5 py-2.5 border-b border-slate-100 dark:border-slate-700/30 flex items-center justify-between gap-3 flex-wrap" data-testid="activity-filter-bar">
          {/* Type filters */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <FilterChip
              active={typeFilter === 'all'}
              onClick={() => { setTypeFilter('all'); setExpanded(false); }}
              label="All"
              count={typeCounts.all}
              data-testid="filter-all"
            />
            {Object.entries(ACTION_CONFIG).map(([key, cfg]) => (
              <FilterChip
                key={key}
                active={typeFilter === key}
                onClick={() => { setTypeFilter(key); setExpanded(false); }}
                label={cfg.label}
                count={typeCounts[key] || 0}
                color={cfg.color}
                activeBg={cfg.activeBg}
                data-testid={`filter-${key}`}
              />
            ))}
          </div>
          {/* Time range */}
          <div className="flex items-center gap-1">
            {TIME_RANGES.map(tr => (
              <button
                key={tr.key}
                onClick={() => { setTimeRange(tr.key); setExpanded(false); }}
                className={`px-2 py-0.5 rounded text-[10px] font-medium transition-all ${
                  timeRange === tr.key
                    ? 'bg-slate-200 dark:bg-white/10 text-slate-800 dark:text-white/80'
                    : 'text-slate-400 dark:text-white/25 hover:text-slate-600 dark:hover:text-white/50'
                }`}
                data-testid={`time-${tr.key}`}
              >
                {tr.label}
              </button>
            ))}
          </div>
        </div>

        {/* Activity list */}
        <div className="px-5 py-1 divide-y divide-slate-100 dark:divide-slate-700/30">
          {visible.length > 0 ? (
            visible.map((item, i) => <ActivityItem key={`${typeFilter}-${timeRange}-${i}`} item={item} index={i} onClick={handleItemClick} />)
          ) : (
            <div className="py-6 text-center">
              <p className="text-xs text-slate-400 dark:text-white/25">No activity for this filter</p>
            </div>
          )}
        </div>

        {/* Expand/collapse toggle */}
        {hasMore && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="w-full flex items-center justify-center gap-1 py-2.5 text-xs text-cyan-500 hover:text-cyan-400 transition-colors border-t border-slate-100 dark:border-slate-700/30"
            data-testid="activity-feed-toggle"
          >
            {expanded ? <><ChevronUp className="h-3.5 w-3.5" /> Show less</> : <><ChevronDown className="h-3.5 w-3.5" /> Show {filtered.length - 5} more</>}
          </button>
        )}
      </div>
    </div>
  );
}

function FilterChip({ active, onClick, label, count, color, activeBg, ...props }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-medium transition-all ${
        active
          ? (activeBg || 'bg-slate-200 dark:bg-white/10 ring-1 ring-slate-300 dark:ring-white/15') + ' text-slate-800 dark:text-white/80'
          : 'text-slate-400 dark:text-white/30 hover:bg-slate-100 dark:hover:bg-white/[0.04] hover:text-slate-600 dark:hover:text-white/50'
      }`}
      {...props}
    >
      {label}
      {count > 0 && (
        <span className={`text-[9px] ${active ? (color || 'text-slate-600 dark:text-white/60') : 'text-slate-300 dark:text-white/20'}`}>
          {count}
        </span>
      )}
    </button>
  );
}
